import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'

import { useModal } from '@context/ModalsContext'
import { useProductReadContext } from '@context/ProductContext'

import { CarouselProvider } from '@ui/Carousel/CarouselProvider'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { GuideTypes } from '@model/ClientGuideDto'
import { WalletTypeEnum } from '@model/WalletDto'

import {
  DiscoverTradeIdeasCarouselChild,
  DownloadTradingCarouselChild,
  EnableTwoFactorAuthCarouselChild,
  FundWalletGuideCarouselChild,
  TickmillMobileAppCarouselChild,
  TransferToTradingAccountCarouselChild,
  TransferWalletToTradingAccountGuideCarouselChild,
} from '@shared/components/FirstTimeGuideCarousel/FirstTimeGuideCarouselChild'
import { GuideConfirmationModal } from '@shared/components/FirstTimeGuideCarousel/modals/GuideConfirmationModal'

import styles from './FirstTimeGuide.module.scss'

interface FirstTimeGuideCarouselProps {
  onConfirmationModalOpen(): void
}

interface FirstTimeGuideCarouselCFDProps {
  isWalletsLoading: boolean
  reloadGuide(): void
  walletType?: WalletTypeEnum
  handleDeposit: () => void
  depositDisabled?: boolean
}

export const FirstTimeGuideCarouselCFD: React.FC<FirstTimeGuideCarouselCFDProps> = (props) => {
  const apiClient = useApiClient(ClientApiClient)
  const { product } = useProductReadContext()
  const { handleDeposit, depositDisabled, isWalletsLoading } = props

  const { data: wallets = [] } = useQuery({
    queryKey: queryKeys.wallet.byType(props.walletType),
    queryFn: () => apiClient.getWallets({ walletTypeId: props.walletType }),
  })

  const defaultWallet = wallets.find((wallet) => wallet.isDefault) || wallets[0]

  const handleGuideDismiss = async () => {
    await apiClient.updateClientGuideDismiss(GuideTypes.IntroductionGuide, {
      tickmillProductId: product,
    })
    props.reloadGuide()
    closeGuideModal.close()
  }

  const closeGuideModal = useModal(GuideConfirmationModal, { onGuideClose: handleGuideDismiss })

  const carousels = [
    { component: <EnableTwoFactorAuthCarouselChild /> },
    {
      component: (
        <FundWalletGuideCarouselChild
          defaultWallet={defaultWallet}
          disabledAction={depositDisabled}
          isWalletsLoading={isWalletsLoading}
          onDeposit={handleDeposit}
        />
      ),
    },
    { component: <TransferWalletToTradingAccountGuideCarouselChild /> },
    {
      component: <TransferToTradingAccountCarouselChild />,
    },
    {
      component: <DownloadTradingCarouselChild />,
    },
    { component: <TickmillMobileAppCarouselChild /> },
    { component: <DiscoverTradeIdeasCarouselChild /> },
  ]

  const carouselSettings = {
    hideDots: true,
    interval: 5000,
    items: carousels,
    loop: false,
  }

  return (
    <div className={styles.wrapper}>
      <CarouselProvider<FirstTimeGuideCarouselProps>
        {...carouselSettings}
        onConfirmationModalOpen={closeGuideModal.open}
      />
    </div>
  )
}
